import { PageHeading, PageTemplate } from 'modules/shared/features/content';
import { GoogleMap, useGoogleMapScriptLoader } from 'modules/shared/features/google-map';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import useProjectData from 'modules/projects/hooks/useProjectData';
// import { updateProjectMap } from '@api/services/project.service';
import ClientMapClientViewControlsPanel from 'modules/client-map/components/ClientMapViewControlsPanel/ClientMapViewControlsPanel';
import { Card } from '@hyperflake/react-ui-library';
import { updateProjectMapPreference } from '@api/services/project.service';

const DEFAULT_VIEW = {
    center: { lat: 0, lng: 0 },
    zoom: 6,
};

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));
const round6 = (n: number) => Math.round(n * 1e6) / 1e6;

const ProjectMap = () => {
    const { clientId, projectId } = useParams();
    const { project, isLoading, setData } = useProjectData(clientId, projectId);

    const { isLoaded } = useGoogleMapScriptLoader();
    const mapRef = useRef<google.maps.Map | null>(null);

    const clickListenerRef = useRef<google.maps.MapsEventListener | null>(null);
    const zoomListenerRef = useRef<google.maps.MapsEventListener | null>(null);
    const centerListenerRef = useRef<google.maps.MapsEventListener | null>(null);
    const isEditingZoomRef = useRef(false);

    const savedView = useMemo(() => {
        const lat = Number(project?.location?.latitude);
        const lng = Number(project?.location?.longitude);
        const z = project?.preferences?.mapZoom;

        return {
            center: Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : DEFAULT_VIEW.center,
            zoom: typeof z === 'number' ? z : DEFAULT_VIEW.zoom,
        };
    }, [project]);

    const initialCenterRef = useRef(savedView.center);
    const initialZoomRef = useRef(savedView.zoom);

    const [latitude, setLatitude] = useState(String(savedView.center.lat));
    const [longitude, setLongitude] = useState(String(savedView.center.lng));
    const [zoom, setZoom] = useState(String(savedView.zoom));
    const [markerPos, setMarkerPos] = useState(savedView.center);
    const [isPicking, setIsPicking] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    const updateProjectMutation = useMutation((payload: any) =>
        updateProjectMapPreference(clientId as string, projectId as string, payload)
    );

    // const updateProjectMapMutation = useMutation(updateProjectMap);

    /* -------------------------------- map listeners -------------------------------- */

    const handleLoad = useCallback((map: google.maps.Map) => {
        mapRef.current = map;

        zoomListenerRef.current = map.addListener('zoom_changed', () => {
            if (isEditingZoomRef.current) return;
            const z = map.getZoom();
            if (z != null) setZoom(z.toFixed(2));
        });

        centerListenerRef.current = map.addListener('center_changed', () => {
            const c = map.getCenter();
            if (!c) return;
            setLatitude(String(round6(c.lat())));
            setLongitude(String(round6(c.lng())));
        });
    }, []);

    /* -------------------------------- pick location -------------------------------- */

    useEffect(() => {
        const map = mapRef.current;
        if (!map || !isPicking) return;

        clickListenerRef.current = map.addListener('click', (e: google.maps.MapMouseEvent) => {
            if (!e.latLng) return;
            const pos = e.latLng.toJSON();

            setMarkerPos(pos);
            setLatitude(String(round6(pos.lat)));
            setLongitude(String(round6(pos.lng)));
            map.panTo(pos);

            setHasUnsavedChanges(true);
            setIsPicking(false);
        });

        return () => clickListenerRef.current?.remove();
    }, [isPicking]);

    /* -------------------------------- inputs -> map -------------------------------- */

    const onLatitudeChange = (v: string) => {
        setLatitude(v);
        setHasUnsavedChanges(true);
        const lat = Number(v);
        const lng = Number(longitude);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
            mapRef.current?.panTo({ lat, lng });
            setMarkerPos({ lat, lng });
        }
    };

    const onLongitudeChange = (v: string) => {
        setLongitude(v);
        setHasUnsavedChanges(true);
        const lat = Number(latitude);
        const lng = Number(v);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
            mapRef.current?.panTo({ lat, lng });
            setMarkerPos({ lat, lng });
        }
    };

    const onZoomChange = (v: string) => {
        isEditingZoomRef.current = true;
        setZoom(v);
        const z = Number(v);
        if (Number.isFinite(z)) {
            mapRef.current?.setZoom(clamp(Math.round(z), 1, 22));
            setHasUnsavedChanges(true);
        }
    };

    /* -------------------------------- actions -------------------------------- */

    const onReset = () => {
        setLatitude(String(savedView.center.lat));
        setLongitude(String(savedView.center.lng));
        setZoom(String(savedView.zoom));
        setMarkerPos(savedView.center);
        setHasUnsavedChanges(false);

        mapRef.current?.panTo(savedView.center);
        mapRef.current?.setZoom(savedView.zoom);
    };

    const onSave = async () => {
        if (!project) return;

        // await updateProjectMapMutation.mutateAsync({
        //     projectId: project._id,
        //     mapZoom: clamp(Number(zoom), 1, 22),
        //     latitude: Number(latitude),
        //     longitude: Number(longitude),
        // });

        setData((prev: any) => ({
            ...prev,
            location: {
                ...prev.location,
                latitude,
                longitude,
            },
            preferences: {
                ...prev.preferences,
                mapZoom: Number(zoom),
            },
        }));

        const payload: any = {
            mapZoom: Number(zoom),
            mapCenter: {
                latitude: latitude,
                longitude: longitude,
            },
        };
        try {
            await updateProjectMutation.mutateAsync({
                preferences: payload, // or payload directly
            });
        } catch (err: any) {
            const message = err?.response?.data?.message || err?.message || 'Failed to save map preference';

            toast.error(message);
        }

        toast.success('Project map updated');
        setHasUnsavedChanges(false);
    };

    const updateFromPosition = useCallback((pos: google.maps.LatLngLiteral) => {
        const lat = round6(pos.lat);
        const lng = round6(pos.lng);

        setMarkerPos({ lat, lng });
        setLatitude(String(lat));
        setLongitude(String(lng));
        setHasUnsavedChanges(true);
    }, []);

    const onMarkerDragEnd = useCallback(
        (pos: google.maps.LatLngLiteral) => {
            updateFromPosition(pos);
            mapRef.current?.panTo(pos);
        },
        [updateFromPosition]
    );

    /* -------------------------------- render -------------------------------- */

    return (
        <PageTemplate>
            {isLoaded && (
                <Card>
                    <Card.Header>
                        <PageHeading>Project Map Preference</PageHeading>
                    </Card.Header>
                    <Card.Body>
                        <div className="relative w-full h-screen">
                            <GoogleMap
                                center={initialCenterRef.current}
                                zoom={initialZoomRef.current}
                                onLoad={handleLoad}
                            >
                                {isPicking && (
                                    <GoogleMap.AdvancedMarker
                                        position={markerPos}
                                        title="Click / drag to change location"
                                        draggable
                                        onMarkerDragEnd={onMarkerDragEnd}
                                        zIndex={20}
                                    >
                                        <div className="relative">
                                            <div className="w-8 h-8 bg-primary rounded-full border-4 border-white shadow-lg flex items-center justify-center animate-pulse">
                                                <svg
                                                    className="w-4 h-4 text-white"
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                                                        clipRule="evenodd"
                                                    />
                                                </svg>
                                            </div>
                                            <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-6 bg-red-500" />
                                        </div>
                                    </GoogleMap.AdvancedMarker>
                                )}
                            </GoogleMap>

                            <ClientMapClientViewControlsPanel
                                title="Map Preference"
                                latitude={latitude}
                                longitude={longitude}
                                zoom={zoom}
                                isPicking={isPicking}
                                hasUnsavedChanges={hasUnsavedChanges}
                                onTogglePick={() => setIsPicking((v) => !v)}
                                onLatitudeChange={onLatitudeChange}
                                onLongitudeChange={onLongitudeChange}
                                onZoomChange={onZoomChange}
                                onZoomFocus={() => (isEditingZoomRef.current = true)}
                                onZoomBlur={() => (isEditingZoomRef.current = false)}
                                onReset={onReset}
                                onSave={onSave}
                                // isSubmitting={isLoading || updateProjectMapMutation.isPending}
                                isSubmitting={isLoading || updateProjectMutation.isPending}
                            />
                        </div>
                    </Card.Body>
                </Card>
            )}
        </PageTemplate>
    );
};

export default ProjectMap;
